<template>
  <WdsStack direction-column gap-custom="2.5rem" class="tw-py-2">
    <BaseSection>
      <BaseSectionHeader :header-text="$t('Remuneration')" />

      <BaseSectionBody>
        <BaseFieldGroup
          :label="$t('Payment')"
          :helper-text="$t('How would you prefer to pay for this job?')"
        >
          <div class="tw-w-full">
            <WdsSelect v-model="rateType" :list="rateOptions" data-test="job-rate-type-dropdown" />
          </div>
        </BaseFieldGroup>

        <BaseFieldGroup
          :label="$t('Budget type')"
          :helper-text="$t('Is this a specific rate or a rate range?')"
          align-center
        >
          <div class="tw-w-full">
            <WdsRadioGroup v-model:checked="store.form.budgetType" row name="job-budget-type">
              <WdsRadio
                :value="BUDGET_TYPE.SPECIFIC_RATE"
                data-test="job-budget-type-specific_rate"
              >
                {{ $t('Specific rate') }}
              </WdsRadio>
              <WdsRadio :value="BUDGET_TYPE.RATE_RANGE" data-test="job-budget-type-rate_range">
                {{ $t('Rate range') }}
              </WdsRadio>
            </WdsRadioGroup>
          </div>
        </BaseFieldGroup>

        <BaseFieldGroup
          v-if="store.form.budgetType === BUDGET_TYPE.SPECIFIC_RATE"
          :label="$t('Rate')"
          :helper-text="$t('What\'s your expected budget for this rate?')"
          optional
        >
          <div class="tw-w-full">
            <WdsInputCurrency
              v-model="store.form.rate"
              :config="inputCurrencyConfig"
              data-test="job-expected-rate"
              class="tw-w-full"
              :error="getErrorMessage(validationStore, 'rate')"
            />
          </div>
        </BaseFieldGroup>

        <template v-else>
          <BaseFieldGroup
            :label="$t('Minimum rate')"
            :helper-text="$t('What\'s your expected minimum rate?')"
          >
            <div class="tw-w-full">
              <WdsInputCurrency
                v-model="store.form.minimumRate"
                :config="inputCurrencyConfig"
                data-test="job-min-rate"
                required
                class="tw-w-full"
                :error="getErrorMessage(validationStore, 'minimumRate')"
              />
            </div>
          </BaseFieldGroup>
          <BaseFieldGroup
            :label="$t('Maximum rate')"
            :helper-text="$t('What\'s your expected maximum budget for this rate?')"
          >
            <div class="tw-w-full">
              <WdsInputCurrency
                v-model="store.form.maximumRate"
                :config="inputCurrencyConfig"
                data-test="job-max-rate"
                required
                class="tw-w-full"
                :error="getErrorMessage(validationStore, 'maximumRate')"
              />
            </div>
          </BaseFieldGroup>
        </template>
      </BaseSectionBody>
    </BaseSection>

    <BudgetTracking
      v-if="!loading"
      :input-currency-config="inputCurrencyConfig"
      :projects-enabled="result?.company?.settings?.projectTracking ?? false"
      :required-job-budget="result?.company?.settings?.requiredJobBudget ?? false"
    />
  </WdsStack>
</template>

<script setup lang="ts">
import {
  WdsInputCurrency,
  WdsRadioGroup,
  WdsRadio,
  WdsSelect,
  WdsStack,
} from '@worksome/wds-components'
import { computed } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { RateType } from '@/gql/graphql'

import { $t } from '@/composables/translation'
import BaseSection from '@/components/BaseSection.vue'
import BaseSectionHeader from '@/components/BaseSectionHeader.vue'
import BaseSectionBody from '@/components/BaseSectionBody.vue'
import BaseFieldGroup from '@/components/BaseFieldGroup.vue'

import { BUDGET_TYPE } from '@/components/Job/constants'
import { getErrorMessage } from '@/components/Job/functions/getErrorMessage'
import { CompanyQuery } from '@/components/Job/graphql/queries'
import { useJobFormStore } from '@/components/Job/Edit/store/jobFormStore'
import { useValidationStore } from '@/components/Job/Edit/store/useValidationStore'
import BudgetTracking from '@/components/Job/Edit/Tabs/BudgetTab/BudgetTracking.vue'

const store = useJobFormStore()
const validationStore = useValidationStore()

const rateType = computed({
  get: () => ({ value: store.form.rateType }),
  set: (selectedRateType) => {
    store.form.rateType = selectedRateType.value
  },
})

const rateOptions = [
  { value: RateType.Hourly, label: $t('Hourly Rate') },
  { value: RateType.Daily, label: $t('Daily Rate') },
  { value: RateType.Weekly, label: $t('Weekly Rate') },
  { value: RateType.Monthly, label: $t('Monthly Rate') },
  { value: RateType.Fixed, label: $t('Fixed Amount') },
]

const { result, loading } = useQuery(
  CompanyQuery,
  {
    companyId: currentAccount.globalId,
  },
  {
    clientId: 'publicApi',
    fetchPolicy: 'no-cache',
  },
)

// @ts-ignore
const { thousandsSeparator, decimalSeparator } = window.currentLocale

const inputCurrencyConfig = computed(() => ({
  currencySymbol: result?.value?.company?.currency || 'USD',
  thousandsSeparator,
  decimalSeparator,
  showFractions: true,
}))
</script>
